import React, { useEffect, useState } from "react"
import { navigate } from "gatsby"
import SEO from "../SEO/SEO"
import CancelModal from "../Modals/CancelModal"
import { fetchApi, postApi } from "../Common/WebApi-utils"
import LoadingModal from "../Modals/LoadingModal"
import { ArrowNarrowLeftIcon, MinusIcon, PlusIcon } from "@heroicons/react/solid"
import { GetLanguageName } from "../Common/Utils"
import MessageModal from "../Modals/MessageModal"

/** Material item from GET GyldendalMaterialGroup. Property name for max must match API when GET is updated. */
type AssessmentCopyMaterial = {
  id: string
  name: string
  subtitle?: string
  iSBN?: string
  language?: string
  isOrder: boolean
  pp_availablefromdate?: Date
  availabilityStatus?: string
  pp_distributionstatus?: string
  businessCategory?: string
  existingOrders?: number
  studentCount?: number
  orderLimitStudentCount?: number
  orderLimit_lower?: number
  orderLimit_higher?: number
}

/**
 * Calculate max order count based on student count and order limits
 */
const calculateMaxOrderCount = (material: AssessmentCopyMaterial): number => {
  const {
    existingOrders = 0,
    studentCount = 0,
    orderLimitStudentCount = 0,
    orderLimit_lower = 1,
    orderLimit_higher = 1,
  } = material

  let allowedOrders = 0
  if (orderLimitStudentCount > studentCount) {
    allowedOrders = orderLimit_lower
  } else {
    allowedOrders = orderLimit_higher
  }

  const maxOrderCount = Math.max(0, allowedOrders - existingOrders)
  return maxOrderCount
}

/** Format ISO date string or Date to DD-MM-YYYY (strip time part). */
const formatDateOnly = (value: Date | string | undefined): string => {
  if (value == null) return ""
  const str = typeof value === "string" ? value : (value as Date).toISOString?.() ?? String(value)
  const [y, m, d] = (str.split("T")[0] ?? "").split("-")
  if (!d) return ""
  return `${d}-${m}-${y}`
}

/**
 * This component is for listing the materials from Dynamics365 to choose from to order
 *
 * @version 1.0.1
 * @visibleName Assessment Copy list
 * @author [Younes Hajji]
 * @author [Henrik Lie]
 */
let redirectURl = "https://gyldendal.no"

const FormAssessmentCopy = ({ account }) => {
  const [modal, setModal] = useState(false)
  const [loading, setLoading] = useState(false)

  const [materialGroup, setMaterialGroup] = useState<AssessmentCopyMaterial[]>([])
  const [showList, setShowList] = useState(true)
  const [selectedQuantities, setSelectedQuantities] = useState<Record<string, number>>({})
  const [businessCategory, setBusinessCategory] = useState("")

  const [errorInfo, setErrorInfo] = useState({
    title: "Oops!",
    message: "Det er registrert en feil i materiellet du forsøker å bestille.",
    buttonText: "Ok",
    email: false,
    showError: false,
  })

  useEffect(() => {
    var url = window.sessionStorage.getItem("gyldendal_minside_redirecturl")
    if (url !== null) redirectURl = url

    setLoading(true)
    let materialGroupId = window.sessionStorage.getItem("gyldendal_minside_groupid")
    if (materialGroupId === "mønster") materialGroupId = "moenster"
    // const materialGroupId = getCookie("gyldendal_minside_groupid")
    const accountId = window.sessionStorage.getItem("gyldendal_minside_accountid")

    const accessToken = window.sessionStorage.getItem("gyldendal_minside_access_token")
    const location = window.sessionStorage.getItem("gyldendal_minside_redirecturl") ?? ""
    fetchApi(
      process.env.GATSBY_API_URL_APIM + "/GyldendalMaterialGroup/" + materialGroupId + "/" + accountId,
      accessToken,
      location
    )
      .then((response) => {
        setShowList(true)
        setMaterialGroup(response.data)
        response.data.forEach((element) => {
          if (element.businessCategory !== null) setBusinessCategory(element.businessCategory)
        })

        if (response.data.length === 0) setShowList(false)
      })
      .catch((error) => {
        setShowList(false)

        if (error.message.includes("404"))
          setErrorInfo(() => ({
            buttonText: "Ok",
            title: "Oops!",
            message:
              error.message +
              ": Enten materialgruppen med id " +
              materialGroupId +
              " finnes ikke, eller du er ikke koblet en skole i Dynamics.",
            email: false,
            showError: true,
          }))
        else
          setErrorInfo(() => ({
            buttonText: "Ok",
            title: "Oops!",
            message: error.message,
            email: false,
            showError: true,
          }))
      })
      .finally(function () {
        setLoading(false)
      })
  }, [account])

  const hasSelection = Object.values(selectedQuantities).some((q) => q > 0)

  const setQuantity = (bookId: string, value: number) => {
    setSelectedQuantities((prev) => ({ ...prev, [bookId]: value }))
  }

  const createOrderArray = () => {
    const orderArray = {
      accountId: window.sessionStorage.getItem("gyldendal_minside_accountid"),
      deliveryTo: window.sessionStorage.getItem("gyldendal_minside_accountname"),
      contactName: window.sessionStorage.getItem("gyldendal_minside_userfullname"),
      businessCategory: businessCategory?.toString(),
      ordreLine: [] as { MaterialId: string; addAsOrder: boolean; orderLineCount: number }[],
    }

    materialGroup.forEach((book) => {
      const qty = selectedQuantities[book.id] ?? 0
      if (qty > 0) {
        orderArray.ordreLine.push({
          MaterialId: book.id,
          addAsOrder: true,
          orderLineCount: qty,
        })
      }
    })

    setLoading(true)
    const location = window.sessionStorage.getItem("gyldendal_minside_redirecturl") ?? ""
    postApi(process.env.GATSBY_API_URL_APIM + "/GyldendalOrders/makeOrder", orderArray, location)
      .then((response) => {
        window.sessionStorage.setItem("gyldendal_minside_orderid", response.data)
        navigate("/app/tilganger")
      })
      .catch((error) => {
        setErrorInfo(() => ({
          buttonText: "Ok",
          title: "Oops!",
          message: "Kunne ikke opprette ordre: " + error.message,
          email: false,
          showError: true,
        }))
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <>
      <div className="flex pt-10 bg-white">
        <SEO title="Bestilling" description="Gyldendal sin hjem side." />
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 m-auto sm:p-2 bg-white">
          {loading ? <LoadingModal /> : null}
          <div className="flex items-center">
            <a
              href={redirectURl}
              className="border-t-2 border-transparent pt-4 pr-1 inline-flex items-center text-sm font-medium text-blue-500 hover:text-red-700 hover:border-red-900"
            >
              <ArrowNarrowLeftIcon className="mr-3 h-5 w-5 text-blue-00" aria-hidden="true" />
              Gå til hjemmesiden
            </a>
          </div>
          {showList ? (
            <div className="bg-white sm:p-2">
              <div>
                <h2 className="text-3xl font-bold pb-3">Forespørsel om vurderingseksemplar</h2>
                <h5 className="text-md">
                  Vi tilbyr et utvalg av våre nyeste titler som gratis vurderingseksemplar til skolen.
                </h5>
                <h5 className="text-md">Hver skole kan kun motta et begrenset antall vurderingseksemplarer.</h5>
              </div>
              <div className="pt-10">
                <h3 className="font-semibold">Velg vurderingseksemplar</h3>
                <div className="max-w-full">
                  {materialGroup.map((book) => {
                    const maxOrder = calculateMaxOrderCount(book)
                    const quantity = selectedQuantities[book.id] ?? 0
                    const canOrder = maxOrder > 0

                    return (
                      <div key={book.id} className="relative flex justify-between border-b p-3">
                        <div className="flex space-x-2">
                          <div className="inline-flex items-center mt-3 box-shadow-none">
                            {!canOrder ? (
                              <input
                                id={book.id}
                                name="unavailable_book"
                                type="checkbox"
                                className="form-checkbox h-7 w-7 text-gray-500 rounded absolute left-1 top-4 disabled:opacity-50"
                                checked
                                disabled
                              />
                            ) : (
                              <input
                                id={book.id}
                                name="available_book"
                                type="checkbox"
                                className="form-checkbox h-7 w-7 text-carmine-500 rounded absolute left-1 top-4 focus:ring-transparent"
                                checked={quantity > 0}
                                onChange={(e) => setQuantity(book.id, e.target.checked ? 1 : 0)}
                              />
                            )}
                          </div>
                          <div>
                            <label htmlFor={book.id} className="ml-3 flex flex-col cursor-pointer pl-5">
                              <span className="block font-semibold text-md ">{book.name}</span>
                              <span className="block text-sm">{book.subtitle}</span>
                              <span className="block text-sm text-gray pt-4 text-gray-600">ISBN: {book.iSBN}</span>
                              <span className="block text-sm text-gray pt-2 text-gray-800">
                                {GetLanguageName(book.language)}
                              </span>
                              {!canOrder ? (
                                <span className="block font-semibold text-md ">
                                  *Skolen din har allerede mottatt maks antall vurderingseksemplarer.{" "}
                                  <a
                                    href="https://www.gyldendal.no/artikler/spoersmal-og-svar-om-vurderingseksemplar/"
                                    className="underline"
                                  >
                                    {" "}
                                    Les mer
                                  </a>
                                </span>
                              ) : null}
                              {canOrder && quantity > 0 && (
                                <div className="flex items-center gap-2 pt-3">
                                  <div className="flex items-center border border-gray-300 rounded">
                                    <button
                                      type="button"
                                      aria-label="Reduser antall"
                                      disabled={quantity <= 1}
                                      onClick={() => setQuantity(book.id, Math.max(1, quantity - 1))}
                                      className="p-1.5 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 rounded-l"
                                    >
                                      <MinusIcon className="h-4 w-4" />
                                    </button>
                                    <span className="min-w-[2rem] text-center py-1 text-sm" aria-live="polite">
                                      {quantity}
                                    </span>
                                    <button
                                      type="button"
                                      aria-label="Øk antall"
                                      disabled={quantity >= maxOrder}
                                      onClick={() => setQuantity(book.id, Math.min(maxOrder, quantity + 1))}
                                      className="p-1.5 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 rounded-r"
                                    >
                                      <PlusIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                  {quantity >= maxOrder && (
                                    <span className="text-sm text-gray-600">
                                      Maks antall ({maxOrder}) for denne titelen er nådd.
                                    </span>
                                  )}
                                </div>
                              )}
                            </label>
                          </div>
                        </div>
                        <div className="justify-end text-right">
                        {book.availabilityStatus == "04" && book.pp_distributionstatus == "21" ? <p className="text-sm pt-4 text-right break-words">På lager. Forventet levering 5-10 virkedager</p> : null}
                          {book.availabilityStatus == "02" && book.pp_distributionstatus == "10" ? <p className="text-sm pt-4 text-right break-words">Kommer. Forventet salgsdato: {formatDateOnly(book.pp_availablefromdate)}</p> : null}
                          {book.availabilityStatus == "04" && book.pp_distributionstatus == "31" ? <p className="text-sm pt-4 text-right break-words">Utsolgt. Nytt opplag ventes: {formatDateOnly(book.pp_availablefromdate)}</p> : null}
                          {book.availabilityStatus == "04" && book.pp_distributionstatus == "33" ? <p className="text-sm pt-4 text-right break-words">Utsolgt. Nytt opplag ventes: {formatDateOnly(book.pp_availablefromdate)}</p> : null}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
              {modal ? <CancelModal proceed={() => setModal(false)} /> : null}

              <div className="flex justify-end">
                <button
                  id="btnCancel"
                  onClick={() => setModal(true)}
                  className="h-10 m-2 underline bg-transparent text-black font-semibold hover:text-gray py-2 px-6 borde-none"
                >
                  Avbryt
                </button>
                <button
                  id="btnSend"
                  disabled={!hasSelection}
                  onClick={createOrderArray}
                  className="h-10 m-2 bg-carmine-500 hover:bg-carmine-800 text-white font-semibold hover:text-white py-2 px-6 border border-red-500 rounded disabled:opacity-50"
                >
                  Send
                </button>
              </div>
            </div>
          ) : (
            <MessageModal {...errorInfo} />
          )}
        </div>
      </div>
    </>
  )
}

export default FormAssessmentCopy

